
import React from "react";

export const T12vsOtherMetrics = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-teal-50 border border-teal-200 rounded-full mb-6">
            <span className="text-teal-600 text-sm font-semibold">Metric Comparison</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            T12 vs Other Financial Metrics
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Understanding how T12 compares to other common real estate financial metrics helps you
            communicate effectively with buyers and lenders.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <div className="aspect-w-4 aspect-h-3 rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/6248533a048b4628a462b293e8102767.jpg"
                alt="Comparison infographic of T12 versus other real estate financial metrics"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="space-y-6">
            {/* T12 vs TTM */}
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">
                  T12 vs TTM (Trailing Twelve Months)
                </h3>
                <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                  Same Thing
                </span>
              </div>
              <p className="text-gray-700 mb-3">
                T12 and TTM are identical concepts - both refer to the most recent 12-month period of
                financial performance. The terms are used interchangeably in commercial real
                estate, though T12 is more common in apartment transactions.
              </p>
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <p className="text-sm text-gray-700">
                  <strong>Bottom Line:</strong> If a buyer asks for TTM, provide your T12. They're
                  asking for the same document.
                </p>
              </div>
            </div>

            {/* T12 vs Pro Forma */}
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">T12 vs Pro Forma</h3>
                <span className="px-3 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full">
                  Very Different
                </span>
              </div>
              <p className="text-gray-700 mb-3">
                <strong>T12</strong> shows actual historical performance based on real data.{" "}
                <strong>Pro Forma</strong> shows projected future performance based on assumptions
                and potential improvements.
              </p>
              <div className="bg-white rounded-lg p-4 border border-gray-200 space-y-2">
                <p className="text-sm text-gray-700">
                  <strong>T12:</strong> "Here's what actually happened over the past 12 months"
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Pro Forma:</strong> "Here's what could happen if we make these changes"
                </p>
              </div>
              <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Warning:</strong> Never mix T12 and pro forma numbers. Buyers will verify
                  T12 against bank statements and tax returns. Inflated numbers kill deals.
                </p>
              </div>
            </div>

            {/* T12 vs YTD */}
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">T12 vs Year-to-Date (YTD)</h3>
                <span className="px-3 py-1 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">
                  Different Period
                </span>
              </div>
              <p className="text-gray-700 mb-3">
                <strong>T12</strong> always covers 12 full months. <strong>YTD</strong> covers
                January 1 through the current date, which could be 1-12 months depending on when
                you're looking.
              </p>
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <p className="text-sm text-gray-700 mb-2">
                  <strong>Example (viewing on June 15, 2024):</strong>
                </p>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• T12: June 1, 2023 - May 31, 2024 (12 months)</li>
                  <li>• YTD: January 1, 2024 - June 15, 2024 (5.5 months)</li>
                </ul>
              </div>
              <div className="mt-3 p-3 bg-teal-50 border border-teal-200 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Buyer Preference:</strong> Buyers prefer T12 because it captures a full
                  year including all seasonal variations. YTD is incomplete and harder to analyze.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Reference Grid */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl p-8 text-white">
          <h3 className="text-2xl font-bold mb-8 text-center">
            Quick Reference: Financial Metric Comparison
          </h3>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* T12 / TTM */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="text-teal-400 font-bold mb-2">T12 / TTM</div>
              <div className="text-sm text-gray-300 mb-3">
                Most recent 12 months of actual performance
              </div>
              <div className="text-xs text-gray-400">
                <strong>Use:</strong> Property valuation, loan underwriting, buyer due diligence
              </div>
            </div>

            {/* Pro Forma */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="text-teal-400 font-bold mb-2">Pro Forma</div>
              <div className="text-sm text-gray-300 mb-3">
                Projected future performance with improvements
              </div>
              <div className="text-xs text-gray-400">
                <strong>Use:</strong> Showing value-add potential, renovation planning
              </div>
            </div>

            {/* YTD */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="text-teal-400 font-bold mb-2">YTD</div>
              <div className="text-sm text-gray-300 mb-3">
                January 1 through current date (partial year)
              </div>
              <div className="text-xs text-gray-400">
                <strong>Use:</strong> Internal tracking, supplemental to T12
              </div>
            </div>

            {/* Annual P&L */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="text-teal-400 font-bold mb-2">Annual P&L</div>
              <div className="text-sm text-gray-300 mb-3">
                Calendar year (Jan 1 - Dec 31) performance
              </div>
              <div className="text-xs text-gray-400">
                <strong>Use:</strong> Tax returns, historical trend analysis
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};